/* eslint-disable complexity */
import React, { useContext, RefObject, useState, useEffect, memo, useCallback } from 'react';
import {
  ReviewerCopilotFeedback,
  ReviewerCopilotResultViewer,
} from '../../../feedback/ReviewerCopilotBox';
import { Spinner } from '@scale/scaleui';
import { StepCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepCard';
import API from '@scale/frontend-lib/dashboard-api';
import { useQuery } from 'react-query';
import useDebouncedValue from 'react-scale/hooks/useDebouncedValue';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { InteractionStepType } from '@scale/shared/types/chat/params';
import { EditorEvents, useCurrentEditor } from '@tiptap/react';
import { usePopper } from 'react-popper';
import { useAllResponsesForStepType } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { CopilotSelection, CopilotTransactions } from '../plugins/ReviewerCopilot';

interface ReviewerCopilotPopoverProps {
  enabled?: boolean;
  anchorRef: RefObject<HTMLElement>;
  isLeftPosition?: boolean;
}

const ReviewerCopilotPopover = ({
  enabled = false,
  anchorRef,
  isLeftPosition = false,
}: ReviewerCopilotPopoverProps) => {
  const { editor } = useCurrentEditor();
  const [isOpen, setIsOpen] = useState(false);
  const [selection, setSelection] = useState<CopilotSelection>({
    from: 0,
    to: 0,
    empty: true,
    text: undefined,
  });

  useEffect(() => {
    if (!editor) return;
    editor?.commands.setCopilotEnabled(enabled);
  }, [editor, enabled]);

  useEffect(() => {
    if (!editor) return;
    function handleTransaction(event: EditorEvents['transaction']) {
      const openChange = event.transaction.getMeta(CopilotTransactions.OPEN_CHANGE);
      const selectedChange = event.transaction.getMeta(CopilotTransactions.SELECTED_CHANGE);
      if (openChange !== undefined) {
        setIsOpen(openChange);
      }
      if (selectedChange !== undefined) {
        setSelection(selectedChange);
      }
    }
    editor.on('transaction', handleTransaction);
    return () => {
      editor.off('transaction', handleTransaction);
    };
  }, [editor]);

  const queryText = useDebouncedValue(selection.text, 400);

  const [feedbackPerResult, setFeedbackPerResult] = React.useState<
    Record<string, ReviewerCopilotFeedback>
  >({});
  const promptInputResponses = useAllResponsesForStepType(InteractionStepType.PromptInput);
  const multiturnContinueResponses = useAllResponsesForStepType(
    InteractionStepType.MultiTurnContinue,
  );
  const referenceTexts = [...promptInputResponses, ...multiturnContinueResponses].flatMap(
    response => response.context.referenceTexts || [],
  );
  const [popperRef, setPopperRef] = useState<HTMLDivElement | null>(null);

  const taskContext = useContext(ChatTaskContext);
  const [claimIndex, setClaimIndex] = React.useState(0);

  const { data, error, isLoading } = useQuery({
    queryFn: async () => {
      if (!queryText || queryText.length <= 15) {
        return Promise.resolve({ results: [], inferenceId: '' });
      }
      return API.genai.fetchReviewerCopilotInference({
        content: queryText,
        taskId: taskContext.taskId,
        projectId: taskContext.projectId,
        assignmentId: taskContext.assignmentId,
        referenceTexts,
      });
    },
    queryKey: [open, queryText],
    staleTime: Infinity,
    enabled: enabled && isOpen,
  });

  useEffect(() => {
    setClaimIndex(0);
    setFeedbackPerResult({});
    if (!queryText) {
      setIsOpen(false);
      editor?.commands.setCopilotOpen(false);
    }
  }, [editor, queryText]);

  const handlePrevious = () => {
    if (claimIndex > 0) {
      setClaimIndex(claimIndex - 1);
    }
  };

  const handleNext = () => {
    if (data?.results && data.results.length && claimIndex < data.results.length - 1) {
      setClaimIndex(claimIndex + 1);
    }
  };

  const handleClose = useCallback(() => {
    if (!editor) return;
    editor.commands.setCopilotOpen(false);
  }, [editor]);

  // Determine the start location of the popper based on the text selection
  const isSelectionPosValid =
    selection &&
    editor &&
    selection.from >= 0 &&
    selection.from <= editor.view.state.doc.content.size;

  const start = isSelectionPosValid ? editor.view.coordsAtPos(selection.from) : undefined;

  const verticalOffset =
    start && anchorRef.current ? start.top - anchorRef.current.getBoundingClientRect().top : 0;

  const { styles, attributes } = usePopper(anchorRef.current, popperRef, {
    placement: isLeftPosition ? 'left-start' : 'right-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [verticalOffset, 28],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          mainAxis: false,
        },
      },
    ],
  });

  if (error || !editor || !enabled || !isOpen) return null;

  return (
    <div ref={setPopperRef} style={styles.popper} {...attributes.popper} className="z-30 flex">
      <StepCard className="w-80">
        <StepCard.Header className="px-3 border-b border-neutral-200">
          <StepCard.HeaderLeft>
            <StepCard.Subtitle>Fact Checker</StepCard.Subtitle>
          </StepCard.HeaderLeft>
          <StepCard.HeaderRight className="gap-1 flex flex-row justify-start">
            {data?.results && data.results.length > 1 && (
              <>
                <StepCard.Button
                  icon="chevron-left"
                  onClick={handlePrevious}
                  disabled={claimIndex === 0}
                />
                <StepCard.Button
                  icon="chevron-right"
                  onClick={handleNext}
                  disabled={claimIndex === data.results.length - 1}
                />
              </>
            )}
            <StepCard.Button icon="times" onClick={handleClose} />
          </StepCard.HeaderRight>
        </StepCard.Header>
        <StepCard.Body className="border-t-0 mb-1">
          {isLoading && (
            <div className="flex items-center justify-center">
              <Spinner size="sm" />
            </div>
          )}
          {data?.results &&
            data.results.length > 0 &&
            claimIndex < data.results.length &&
            data.inferenceId && (
              <ReviewerCopilotResultViewer
                inferenceId={data.inferenceId}
                reviewerCopilotResult={data.results[claimIndex]}
                hasFeedback={!!feedbackPerResult[data.results[claimIndex].id]}
                setFeedback={feedback => {
                  setFeedbackPerResult(oldFeedback => ({
                    ...oldFeedback,
                    [data.results[claimIndex].id]: feedback,
                  }));
                }}
              />
            )}
          {data?.results && (!queryText || queryText.length <= 15) && (
            <div className="flex items-center justify-center">
              <div>The selection must be at least 15 characters long!</div>
            </div>
          )}
        </StepCard.Body>
      </StepCard>
    </div>
  );
};

export default memo(ReviewerCopilotPopover);
